import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Typography, Card, Tooltip, Space } from 'antd';
import { PhoneOutlined, InfoCircleOutlined, CheckCircleTwoTone, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { putNurseProfile } from '../../../api';
import { useToast } from '../../../ui/ToastProvider';

const ContactInfoStep = ({ onNext, onBack, initial, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const { notify } = useToast();

  // Format phone number as user types (international format)
  const formatPhoneNumber = (value) => {
    const cleanedValue = (value || '').replace(/[^0-9\s\-()+ ]/g, '');
    if (cleanedValue.startsWith('+')) {
      return cleanedValue;
    }
    const phoneNumber = cleanedValue.replace(/[^\d]/g, '');
    if (
      phoneNumber.length >= 10 &&
      phoneNumber.length <= 11 &&
      (phoneNumber.length === 10 || phoneNumber.startsWith('1'))
    ) {
      const usNumber = phoneNumber.length === 11 ? phoneNumber.slice(1) : phoneNumber;
      if (usNumber.length <= 3) {
        return usNumber;
      } else if (usNumber.length <= 6) {
        return `(${usNumber.slice(0, 3)}) ${usNumber.slice(3)}`;
      } else {
        return `(${usNumber.slice(0, 3)}) ${usNumber.slice(3, 6)}-${usNumber.slice(6, 10)}`;
      }
    }
    return cleanedValue;
  };

  const validateUSNumber = (tenDigitNumber) => {
    if ((tenDigitNumber || '').length !== 10) {
      setPhoneError('US phone numbers must be exactly 10 digits');
      setIsPhoneValid(false);
      return false;
    }
    const areaCode = tenDigitNumber.slice(0, 3);
    const exchangeCode = tenDigitNumber.slice(3, 6);
    const subscriberNumber = tenDigitNumber.slice(6, 10);
    if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
      setPhoneError('Area code cannot start with 0 or 1');
      setIsPhoneValid(false);
      return false;
    }
    if (exchangeCode.startsWith('0') || exchangeCode.startsWith('1')) {
      setPhoneError('Exchange code cannot start with 0 or 1');
      setIsPhoneValid(false);
      return false;
    }
    if (areaCode === '911' || exchangeCode === '911') {
      setPhoneError('Invalid phone number format');
      setIsPhoneValid(false);
      return false;
    }
    if (areaCode === '000' || exchangeCode === '000' || subscriberNumber === '0000') {
      setPhoneError('Invalid phone number - too many zeros');
      setIsPhoneValid(false);
      return false;
    }
    setPhoneError('');
    setIsPhoneValid(true);
    return true;
  };

  // Validate US/international format
  const validatePhoneNumber = useCallback((phone) => {
    if (!phone || phone.trim() === '') {
      setPhoneError('Phone number is required');
      setIsPhoneValid(false);
      return false;
    }
    const cleanPhone = phone.replace(/[^\d]/g, '');
    if (phone.trim().startsWith('+1')) {
      if (cleanPhone.length !== 11 || !cleanPhone.startsWith('1')) {
        setPhoneError('Invalid US phone number format with +1');
        setIsPhoneValid(false);
        return false;
      }
      return validateUSNumber(cleanPhone.slice(1));
    }
    if (cleanPhone.length === 10) {
      return validateUSNumber(cleanPhone);
    }
    if (phone.trim().startsWith('+')) {
      if (cleanPhone.length < 7 || cleanPhone.length > 15) {
        setPhoneError('International phone number must be 7-15 digits');
        setIsPhoneValid(false);
        return false;
      }
      const internationalRegex = /^\+[1-9]\d{6,14}$/;
      if (!internationalRegex.test(phone.replace(/[\s\-() ]/g, ''))) {
        setPhoneError('Invalid international phone number format');
        setIsPhoneValid(false);
        return false;
      }
      setPhoneError('');
      setIsPhoneValid(true);
      return true;
    }
    // Accept non-plus numbers with 7-15 digits as valid (e.g., local numbers like 555555555)
    if (cleanPhone.length >= 7 && cleanPhone.length <= 15) {
      setPhoneError('');
      setIsPhoneValid(true);
      return true;
    }
    setPhoneError('Phone number must be 7-15 digits');
    setIsPhoneValid(false);
    return false;
  }, []);

  // Compute validity without surfacing errors (used on initial prefill)
  const isValidPhoneSilent = useCallback((phone) => {
    if (!phone) return false;
    const cleanPhone = phone.replace(/[^\d]/g, '');
    if (phone.trim().startsWith('+1')) {
      return cleanPhone.length === 11 && cleanPhone.startsWith('1') && validateUSNumber(cleanPhone.slice(1));
    }
    if (cleanPhone.length === 10) {
      return validateUSNumber(cleanPhone);
    }
    if (phone.trim().startsWith('+')) {
      if (cleanPhone.length < 7 || cleanPhone.length > 15) return false;
      const internationalRegex = /^\+[1-9]\d{6,14}$/;
      return internationalRegex.test(phone.replace(/[\s\-() ]/g, ''));
    }
    return cleanPhone.length >= 7 && cleanPhone.length <= 15;
  }, []);

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const formatted = formatPhoneNumber(raw);
    form.setFieldsValue({ phone: formatted });
    setHasInteracted(true);
    validatePhoneNumber(formatted);
  };
  useEffect(() => {
    let phoneToUse = initial?.phone;
    if (!phoneToUse) {
      const stored = localStorage.getItem('signupPhoneNumber');
      if (stored) phoneToUse = stored;
    }
    const formatted = formatPhoneNumber(phoneToUse || '');
    form.setFieldsValue({ phone: formatted });
    if (formatted) {
      // Validate silently on prefill to avoid showing errors prematurely
      const ok = isValidPhoneSilent(formatted);
      setIsPhoneValid(!!ok);
      setPhoneError('');
    } else {
      setIsPhoneValid(false);
      setPhoneError('');
    }
  }, [initial, isValidPhoneSilent]);
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const ok = validatePhoneNumber(values.phone);
      if (!ok) {
        setLoading(false);
        return;
      }
      await putNurseProfile({ phone: values.phone });
      if (onSaved) onSaved({ phone: values.phone });
      notify({ title: 'Saved', description: 'Your contact info was updated.', type: 'success' });
      onNext();
    } catch (e) {
      notify({ title: 'Failed to save', description: 'Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  const getPrimaryBtnStyle = (disabled) => ({
    width: '80%',
    height: 60,
    fontSize: 18,
    ...(disabled
      ? { opacity: 0.6, cursor: 'not-allowed' }
      : { backgroundColor: '#004d54', borderColor: '#004d54', boxShadow: '0 6px 16px rgba(0,77,84,0.24)' })
  });
  return (
    <Card style={{ border: 'none', padding: 0, margin: 0, }} >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Contact information</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Add a valid phone number so facilities can reach you. Use US format or an international number with + country code.
        </Typography.Paragraph>
      </Space>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="phone"
          label="Mobile number"
          rules={[{ required: true, message: 'Phone number is required' }]}
          validateStatus={hasInteracted ? (phoneError ? 'error' : (isPhoneValid ? 'success' : undefined)) : undefined}
          help={hasInteracted ? (phoneError || undefined) : undefined}
          extra={!phoneError ? 'Format: (555) 555-5555  •  International: +1 555 555 5555' : undefined}
        >
          <Input
            size="large"
            allowClear
            prefix={<PhoneOutlined style={{ color: '#8c8c8c' }} />}
            onChange={handlePhoneChange}
            placeholder="e.g. (555) 555-5555 or +1 555 555 5555"
            style={{ height: 60 }}
            suffix={
              isPhoneValid ? (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
              ) : (
                <Tooltip title={phoneError || 'Enter your phone number'}>
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              )
            }
          />
        </Form.Item>

        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onBack ? (
            <Button htmlType="button" onClick={onBack} type="text" icon={<ArrowLeftOutlined />} style={{ color: '#667085', fontSize: 16 }}>Back</Button>
          ) : <div />}
          <Button type="primary" htmlType="submit" style={{
            height: 48,
            padding: '0 32px',
            fontSize: 16,
            borderRadius: 8,
            backgroundColor: '#004d54',
            borderColor: '#004d54',
            boxShadow: '0 4px 12px rgba(0, 77, 84, 0.15)'
          }} loading={loading}>
            Continue
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default ContactInfoStep;


